import React, { useEffect, useRef, useState } from 'react';
import {
    Autocomplete, Box, Breadcrumbs, Button, Card, CardActions, CardContent, CircularProgress, debounce, Dialog, DialogContent, DialogTitle, Divider, FormControl,
    FormControlLabel, FormLabel, Grid, IconButton, InputAdornment, Link, List, ListItem, ListItemText, Radio, RadioGroup, Table, TableBody, TableCell, TableHead, TableRow, TextField,
    Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PrintIcon from '@mui/icons-material/Print';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import HomeIcon from '@mui/icons-material/Home';
import EventIcon from '@mui/icons-material/Event';
import PhoneIcon from '@mui/icons-material/Phone';
import MailIcon from '@mui/icons-material/Mail';
import ApartmentIcon from '@mui/icons-material/Apartment';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axiosInstance from '../../lib/axios';
import dayjs from 'dayjs';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import CloseIcon from '@mui/icons-material/Close';

const Directory = () => {
    const [value, setValue] = useState('N');
    const [resRecords, setResRecord] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [propertyId, setPropertId] = useState(localStorage.getItem('selectedPropertyId'));
    const navigate = useNavigate();
    const contentRef = useRef(null);
    const handlePrint = useReactToPrint({ contentRef });
    const [openDialog, setOpenDialog] = useState(false);
    const [moreInfo, setMoreInfo] = useState({});
    const [suggestions, setSuggestions] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [inputValue, setInputValue] = useState('');

    const fetchSuggestions = useRef(
        debounce(async (query) => {
            if (query.trim().length < 2) {
                setSuggestions([]);
                return;
            }

            setLoadingSuggestions(true);

            try {
                const currentDate = dayjs().format('YYYY-MM-DD');
                const flag = value === 'M' ? 'ByMobile' : 'ByName';

                const response = await axiosInstance.post('AccDashboard/AccDashboard', {
                    PropId: propertyId || 2,
                    FromDt: currentDate,
                    ToDt: currentDate,
                    Flag: flag,
                    start: 1,
                    PageSize: 10,
                    SearchText: query.trim(),
                });

                if (response.data.Status === 0 && Array.isArray(response.data.Data)) {
                    const items = response.data.Data.map((r) =>
                        value === 'M' ? r.MobileNo : r.ResidentName
                    );
                    setSuggestions(items);
                } else {
                    setSuggestions([]);
                }
            } catch (err) {
                console.error(err);
                setSuggestions([]);
            } finally {
                setLoadingSuggestions(false);
            }
        }, 500)
    ).current;

    const handleRadioChange = (e) => {
        setValue(e.target.value);
        setSearchText('');
        setResRecord([]);
        setSuggestions([]);
        setResRecord([]);
    };

    const handleSearchChange = (e) => {
        const inputValue = e.target.value;

        if (value === 'M') {
            const newText = inputValue.replace(/\D/g, '');
            setSearchText(newText);
        } else if (value === 'N') {
            const newText = inputValue.replace(/[0-9]/g, '');
            setSearchText(newText);
        }
    };

    const handleClearSearch = () => {
        setSearchText('');
        setResRecord([]);
        setSuggestions([]);
        setResRecord([]);
    };

    const handleSearchClick = async () => {
        if (searchText.trim() === '') {
            toast.warn("Please enter a search term");
            return;
        };
        if (value === 'M' && searchText.trim().length < 10) {
            toast.warn("Mobile number must be at least 10 digits.");
            return;
        }
        try {
            const currentDate = dayjs().format('YYYY-MM-DD');
            const response = await axiosInstance.post('AccDashboard/AccDashboard', {
                PropId: propertyId || 2,
                FromDt: currentDate,
                ToDt: currentDate,
                Flag: value === 'M' ? "ByMobile" : "ByName",
                start: 1,
                PageSize: 50,
                SearchText: searchText
            });
            if (response.data.Status === 0) {
                setResRecord(response.data.Data);
            } else {
                setResRecord([]);
            }
        } catch (error) {
            toast.error("Error while fetching the record.");
        }
    };

    const handleClose = () => {
        setOpenDialog(false);
    };

    const handleViewMore = async (residentId) => {
        setOpenDialog(true);

        try {
            const response = await axiosInstance.post("ResidentMst/RetriveResidentMstAll", {
                ResidentId: residentId,
                Flag: "R",
                Mobile: ""
            });
            if (response.data.Status === 0) {
                setMoreInfo(response.data.Data.MstResidentList[0]);
            } else {
                setMoreInfo([]);
            }
        } catch (error) {
            toast.error("Error while fetching more data.");
        }
    };

    return (
        <Box sx={{
            width: '100%',
            height: '90vh',
            padding: '10px',
            backgroundColor: 'rgb(249, 249, 249)',
            position: 'relative'
        }}>
            <Box
                sx={{
                    backgroundColor: "#ffffff",
                    padding: { xs: "8px 16px", sm: "12px 16px" },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                    borderRadius: "12px",
                    margin: "10px auto",
                    maxWidth: "95%",
                    flexDirection: { xs: "column", sm: "column", md: "row" },
                    gap: 2,
                }}
            >
                <Breadcrumbs
                    aria-label="breadcrumb"
                    separator="›"
                    sx={{
                        fontSize: { xs: "14px", sm: "16px" },
                        display: "flex",
                        flexDirection: { xs: "column", sm: "column", md: "row" }
                    }}
                >
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
                        onClick={() => navigate('/dashboard')}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        <Box component="span">Home</Box>
                    </Link>
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500" }}
                        onClick={() => navigate('/utilities')}
                    >
                        Utilities & Others
                    </Link>

                    <Typography color="text.primary" sx={{ fontWeight: "500" }}>
                        Directory
                    </Typography>
                </Breadcrumbs>
            </Box>

            <Box sx={{ p: 3, backgroundColor: '#f9f9f9', borderRadius: 2, boxShadow: 2, minHeight: '100vh' }}>
                <ToastContainer />
                {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <ApartmentIcon sx={{ fontSize: 30, color: '#635bff' }} />
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                            mb: 0,
                            fontFamily: "'Roboto', sans-serif",
                            letterSpacing: 0.5,
                            background: 'linear-gradient(45deg, #FF5733, #C70039, #900C3F, #581845)',
                            WebkitBackgroundClip: 'text',
                            backgroundClip: 'text',
                            color: 'transparent',
                        }}
                    >
                        Directory
                    </Typography>
                </Box> */}
                <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormLabel id="demo-controlled-radio-buttons-group" sx={{ mr: 2, fontSize: '16px', fontWeight: '600', color: '#635bff' }}>
                                    Search By:
                                </FormLabel>
                                <RadioGroup
                                    row
                                    aria-labelledby="demo-controlled-radio-buttons-group"
                                    name="controlled-radio-buttons-group"
                                    value={value}
                                    onChange={handleRadioChange}
                                    sx={{ flexDirection: 'row', alignItems: 'center' }}
                                >
                                    <FormControlLabel value="N" control={<Radio size='small' />} label="Name" />
                                    <FormControlLabel value="M" control={<Radio size='small' />} label="Mobile" />
                                </RadioGroup>
                            </Box>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Autocomplete
                            freeSolo
                            disableClearable
                            value={searchText}
                            onChange={(event, newValue) => {
                                if (newValue) {
                                    setSearchText(newValue);
                                    setInputValue(newValue);
                                }
                            }}
                            inputValue={inputValue}
                            onInputChange={(event, newInputValue) => {
                                setInputValue(newInputValue);
                                setSearchText(newInputValue);
                                fetchSuggestions(newInputValue);
                            }}
                            options={suggestions}
                            loading={loadingSuggestions}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={value === 'N' ? 'Search by Name...' : 'Search by mobile...'}
                                    variant="outlined"
                                    size="small"
                                    sx={{ bgcolor: 'white', borderRadius: 1 }}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingSuggestions ? (
                                                    <CircularProgress color="inherit" size={20} />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                                {searchText && (
                                                    <IconButton size="small" onClick={handleClearSearch}>
                                                        <ClearIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={6} container spacing={1} justifyContent="flex-start">
                        <Grid item>
                            <span>
                                <Tooltip title="Click to search" arrow>
                                    <Button
                                        variant="contained"
                                        onClick={handleSearchClick}
                                        disabled={searchText.length === 0}
                                        sx={{
                                            backgroundColor: '#635bff',
                                            color: 'white',
                                            textTransform: 'uppercase',
                                            fontWeight: 'bold',
                                            borderRadius: '8px',
                                            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                                            '&:hover': {
                                                backgroundColor: '#5048e4',
                                                boxShadow: '0px 6px 8px rgba(0, 0, 0, 0.2)',
                                            },
                                        }}
                                    >
                                        <SearchIcon />
                                    </Button>
                                </Tooltip>
                            </span>
                        </Grid>

                        <Grid item>
                            <span>
                                <Tooltip title="Print Records" arrow>
                                    <Button
                                        variant="contained"
                                        onClick={handlePrint}
                                        disabled={resRecords.length === 0}
                                        sx={{
                                            backgroundColor: '#00bcd4',
                                            color: 'white',
                                            textTransform: 'uppercase',
                                            fontWeight: 'bold',
                                            borderRadius: '8px',
                                            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                                            '&:hover': {
                                                backgroundColor: '#00acc1',
                                                boxShadow: '0px 6px 8px rgba(0, 0, 0, 0.2)',
                                            },
                                        }}
                                    >
                                        <PrintIcon />
                                    </Button>
                                </Tooltip>
                            </span>
                        </Grid>

                        <Grid item>
                            <Button
                                variant="outlined"
                                color="warning"
                                onClick={() => window.open('/raise-service-tkt', '_blank')}
                                sx={{
                                    color: '#ff9800',
                                    borderColor: '#ff9800',
                                    textTransform: 'uppercase',
                                    fontWeight: 'bold',
                                    borderRadius: '8px',
                                    '&:hover': {
                                        borderColor: '#f57c00',
                                        color: '#f57c00',
                                        backgroundColor: 'rgba(255, 152, 0, 0.1)',
                                    },
                                }}
                            >
                                Raise Ticket
                            </Button>
                        </Grid>

                        <Grid item>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => window.open('/advicegeneratetable', '_blank')}
                                sx={{
                                    color: '#f44336',
                                    borderColor: '#f44336',
                                    textTransform: 'uppercase',
                                    fontWeight: 'bold',
                                    borderRadius: '8px',
                                    '&:hover': {
                                        borderColor: '#e53935',
                                        color: '#e53935',
                                        backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                    },
                                }}
                            >
                                PytAdv
                            </Button>
                        </Grid>

                        <Grid item>
                            <Button
                                variant="outlined"
                                color="success"
                                onClick={() => {
                                    let query = '';
                                    if (value === 'M' && searchText.trim() !== '') {
                                        query = `?mobile=${encodeURIComponent(searchText)}`;
                                    }
                                    window.open(`/reports/resident-account${query}`, '_blank');
                                }}
                                sx={{
                                    color: '#4caf50',
                                    borderColor: '#4caf50',
                                    textTransform: 'uppercase',
                                    fontWeight: 'bold',
                                    borderRadius: '8px',
                                    '&:hover': {
                                        borderColor: '#388e3c',
                                        color: '#388e3c',
                                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                    },
                                }}
                            >
                                Account
                            </Button>
                        </Grid>

                        <Grid item>
                            <span>
                                <Tooltip title="Back" arrow>
                                    <Button
                                        variant="contained"
                                        onClick={() => navigate('/utilities')}
                                        sx={{
                                            backgroundColor: '#ff5722',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            borderRadius: '8px',
                                            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                                            '&:hover': {
                                                backgroundColor: '#e64a19',
                                                boxShadow: '0px 6px 8px rgba(0, 0, 0, 0.2)',
                                            },
                                        }}
                                    >
                                        <ArrowBackIcon />
                                    </Button>
                                </Tooltip>
                            </span>
                        </Grid>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 3 }} ref={contentRef}>
                    <Grid container spacing={2}>
                        {resRecords.map((record) => (
                            <Grid item xs={12} sm={6} md={4} key={record.ResidentId}>
                                <Card sx={{ boxShadow: 5, borderRadius: 2, transition: 'transform 0.3s ease', position: 'relative' }}>
                                    {moreInfo.Photo && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                width: 80,
                                                height: 80,
                                                overflow: 'hidden',
                                                borderRadius: '8px',
                                                border: '2px solid #e3f2fd',
                                                backgroundColor: '#fff',
                                                boxShadow: 2,
                                            }}
                                        >
                                            <img
                                                src={moreInfo.Photo}
                                                alt="Resident"
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                }}
                                            />
                                        </Box>
                                    )}

                                    <CardContent>
                                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, color: '#1976d2' }}>
                                            {record.ResidentName}
                                        </Typography>

                                        <Grid container spacing={1} sx={{ mb: 1 }}>
                                            <Grid item xs={12}>
                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <HomeIcon sx={{ color: '#1976d2', mr: 1 }} />
                                                    <strong>Property: </strong> <span style={{ marginLeft: '8px' }}>{record.PropName}</span>
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <ConfirmationNumberIcon sx={{ color: '#1976d2', mr: 1 }} />
                                                    <strong>BookNo:</strong> <span style={{ marginLeft: '8px' }}>{record.DocNo}</span>
                                                    <span style={{ margin: '0 8px' }}>|</span>
                                                    <strong>BookDt:</strong> <span style={{ marginLeft: '8px' }}>{dayjs(record.DocDt).format('DD-MM-YYYY')}</span>
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <MeetingRoomIcon sx={{ color: '#1976d2', mr: 1 }} />
                                                    <strong>Room Type: </strong><span style={{ marginLeft: '8px' }}> {record.RoomTypeName}</span>
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <ConfirmationNumberIcon sx={{ color: '#1976d2', mr: 1 }} />
                                                    <strong>Room No: </strong> <span style={{ marginLeft: '8px' }}>{record.RoomNo || 'NA'}</span>
                                                    <span style={{ margin: '0 8px' }}>|</span>
                                                    <strong>BookSt:</strong> <span style={{ marginLeft: '8px' }}>{record.DBFLAG}</span>
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <EventIcon sx={{ color: '#1976d2', mr: 1 }} />
                                                    <strong>Check-in Date: </strong> <span style={{ marginLeft: '8px' }}>{dayjs(record.CheckinDt).format('DD-MM-YYYY')}</span>
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <PhoneIcon sx={{ color: '#1976d2', mr: 1 }} />
                                                    <strong>Mobile No: </strong> <span style={{ marginLeft: '8px' }}>{record.MobileNo}</span>
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <MailIcon sx={{ color: '#1976d2', mr: 1 }} />
                                                    <strong>Email: </strong> <span style={{ marginLeft: '8px' }}>{record.Email}</span>
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <LocationOnIcon sx={{ color: '#1976d2', mr: 1 }} />
                                                    <strong>Address: </strong> <span style={{ marginLeft: '8px' }}>{record.Addr || 'NA'}</span>
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </CardContent>

                                    <CardActions sx={{ justifyContent: 'right', px: 1, py: 0, pt: 0 }}>
                                        <Button variant="text" color="primary"
                                            onClick={() => handleViewMore(record.ResidentId)}
                                            sx={{ fontSize: '0.875rem', textTransform: 'none' }}
                                        >
                                            View More
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Dialog open={openDialog} onClose={handleClose} maxWidth='md' fullWidth>
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                        Resident Info
                        <IconButton onClick={handleClose}>
                            <CloseIcon color="error" />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        {moreInfo ? (
                            <Grid container spacing={1} sx={{ '& .MuiGrid-item': { paddingTop: '4px' } }}>
                                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    {moreInfo.Photo && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                borderRadius: '8px',
                                                width: 100,
                                                height: 100,
                                                overflow: 'hidden',
                                                position: 'absolute',
                                                border: '2px solid #e3f2fd',
                                            }}
                                        >
                                            <img
                                                src={moreInfo.Photo}
                                                alt="Resident"
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="body2" fontWeight="bold" color='primary'>Resident Info:</Typography>
                                    <Grid container spacing={1}>
                                        <Grid item xs={12} sm={6}>
                                            <List sx={{ padding: 0 }}>
                                                <ListItem sx={{ padding: 0 }}>
                                                    <ListItemText primary={`First Name: ${moreInfo.FirstName || 'N/A'}`} />
                                                </ListItem>
                                            </List>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <List sx={{ padding: 0 }}>
                                                <ListItem sx={{ padding: 0 }}>
                                                    <ListItemText primary={`Last Name: ${moreInfo.LastName || 'N/A'}`} />
                                                </ListItem>
                                            </List>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider color='secondary' />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="body2" fontWeight="bold" color='primary'>Resident Contact Info:</Typography>
                                    <Grid container spacing={1}>
                                        <Grid item xs={12} sm={6}>
                                            <List sx={{ padding: 0 }}>
                                                <ListItem sx={{ padding: 0 }}>
                                                    <ListItemText primary={`Phone: ${moreInfo.Mobile || 'N/A'}`} />
                                                </ListItem>
                                            </List>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <List sx={{ padding: 0 }}>
                                                <ListItem sx={{ padding: 0 }}>
                                                    <ListItemText primary={`Email: ${moreInfo.Email || 'N/A'}`} />
                                                </ListItem>
                                            </List>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider color='secondary' />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="body2" fontWeight="bold" color="primary">Family Info:</Typography>
                                    {moreInfo.mstResidentDtlEntities && moreInfo.mstResidentDtlEntities.length > 0 ? (
                                        <Table sx={{ minWidth: 650, padding: 0 }} aria-label="family info table">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: '#e3f2fd', '& th': { padding: '4px 8px', color: '#1976d2' } }}>
                                                    <TableCell sx={{ padding: '4px 8px', fontWeight: 'bold' }}>Relative Name</TableCell>
                                                    <TableCell sx={{ padding: '4px 8px', fontWeight: 'bold' }}>Relation Name</TableCell>
                                                    <TableCell sx={{ padding: '4px 8px', fontWeight: 'bold' }}>Mobile</TableCell>
                                                    <TableCell sx={{ padding: '4px 8px', fontWeight: 'bold' }}>Email</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {moreInfo.mstResidentDtlEntities.map((relative, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell sx={{ padding: '4px 8px' }}>{relative.RelativeName || 'N/A'}</TableCell>
                                                        <TableCell sx={{ padding: '4px 8px' }}>{relative.RelationName || 'N/A'}</TableCell>
                                                        <TableCell sx={{ padding: '4px 8px' }}>{relative.Mobile || 'N/A'}</TableCell>
                                                        <TableCell sx={{ padding: '4px 8px' }}>{relative.Email || 'N/A'}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    ) : (
                                        <Typography variant="body2">No family details available</Typography>
                                    )}
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider color='secondary' />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="body2" fontWeight="bold" color='primary'>Address:</Typography>
                                    <Typography variant="body2">{moreInfo.AreaName || 'N/A'}</Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider color='secondary' />
                                </Grid>
                            </Grid>
                        ) : (
                            <Typography variant="body2">No details available</Typography>
                        )}
                    </DialogContent>
                </Dialog>
            </Box>
        </Box>
    );
};

export default Directory;
